import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentType,
  ReactNode,
} from "react";

import Link, { LinkProps } from "next/link";

import styles from "./_Button.module.scss";

type BaseProps = {
  children: ReactNode;
  className?: string;
};

// Button accepts an 'as' prop to render either a `button`, `a`, or NextJS `Link` component
type ButtonAsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    as?: "button";
    isLoading?: boolean;
  };

type ButtonAsAnchor = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    as: "a";
  };

type ButtonAsLink = BaseProps &
  Omit<LinkProps, keyof BaseProps> & {
    as: "link";
    hrefAs?: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsLink;

function Button(props: ButtonProps) {
  if (props.as === "link") {
    const { as, hrefAs, ...rest } = props;

    return (
      <Link
        // `Link` has its own `as` prop used to present a different URL
        as={hrefAs}
        {...rest}
      />
    );
  }

  if (props.as === "a") {
    const { as, ...rest } = props;

    return <a {...rest} />;
  }

  const { as, isLoading, ...rest } = props;

  return <button disabled={isLoading} {...rest} />;
}

type IconProps = {
  Component: ComponentType<any>;
};

function Icon({ Component }: IconProps) {
  return <Component className={styles.icon} />;
}

Button.Icon = Icon;

export default Button;
